import { FC } from "react";
import { useAtomValue } from "jotai";
import { Chip } from "@mui/material";
import ShareLinkButton from "@/components/share-link-button/ShareLinkButton";
import { positiveState, negativeState, neutralState } from "@/lib/store";
import { EarningsFinancials, EventListRow, MixedEventStat } from "@/models/event-page";
import { buildHeroMetrics, eventStatus, formatEventDate, formatEventTimeLocal, formatPct, isEarningsRow } from "./helpers";
import styles from "./EventPageRevamp.module.scss";

interface EventHeroProps {
    event: EventListRow;
    financials?: EarningsFinancials;
    /** Mixed-event stat row for this event (drives the price reaction for non-earnings). */
    mixedStat?: MixedEventStat;
    /** The address bar links to this event, so offer to copy (web) or share (apps) it. */
    shareable?: boolean;
}

/**
 * Event detail hero: kicker (reported / upcoming + type), title, date, the
 * beat/miss metric cards (EPS / Revenue / EBIT / Net Income) and the price
 * reaction row. Non-earnings events render a lighter note.
 */
const EventHero: FC<EventHeroProps> = ({ event, financials, mixedStat, shareable = false }) => {
    const positive = useAtomValue(positiveState);
    const negative = useAtomValue(negativeState);
    const neutral = useAtomValue(neutralState);

    const surpriseColor = (result: string): string => (result === "beat" ? positive : result === "miss" ? negative : neutral);

    const status = eventStatus(event);
    const isEarnings = isEarningsRow(event);
    const metrics = buildHeroMetrics(financials);
    const isUpcoming = status !== "reported";

    const nextDay = mixedStat?.["1DayAfter"] ?? null;
    const weekAfter = mixedStat?.["1WeekAfter"] ?? null;

    return (
        <section className={styles.hero}>
            <div>
                <div className={styles.heroKicker}>
                    <span className={isUpcoming ? styles.heroDotAccent : styles.heroDotRep} />
                    {isUpcoming ? "UPCOMING" : "REPORTED"} · {(event.type || "").toUpperCase()}
                    {shareable && (
                        <span className={styles.heroShare}>
                            <ShareLinkButton title={event.title || "Event"} />
                        </span>
                    )}
                </div>
                <h1 className={styles.heroTitle}>{event.title || "Event"}</h1>
                {event.date ? (
                    <time className={styles.heroWhen} dateTime={new Date(event.date).toISOString()}>
                        {formatEventDate(event.date)} · {formatEventTimeLocal(event.date)}
                    </time>
                ) : (
                    <div className={styles.heroWhen}>{formatEventDate(event.date)}</div>
                )}
            </div>

            {isEarnings && metrics.length > 0 ? (
                <div className={styles.heroMetrics}>
                    {metrics.map((m) => (
                        <div key={m.label} className={styles.metricTile}>
                            <span className={styles.metricLabel}>{m.label}</span>
                            <span className={styles.metricValue}>{m.actual}</span>
                            <div className={styles.metricSub}>
                                <span className={styles.metricEst}>est {m.estimate}</span>
                                {m.surprisePct != null && m.result && (
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        label={`${m.result === "beat" ? "▲" : m.result === "miss" ? "▼" : "•"} ${formatPct(Math.abs(m.surprisePct), false)} ${m.result}`}
                                        sx={{ height: 18, fontSize: 11, fontWeight: 700, color: surpriseColor(m.result), borderColor: surpriseColor(m.result) }}
                                    />
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className={styles.heroNote}>No reported financial metrics for this event type. Filings and outcomes appear here when published.</div>
            )}

            {(nextDay != null || weekAfter != null) && (
                <div className={styles.heroReaction}>
                    <span className={styles.metricLabel}>Price reaction</span>
                    <div className={styles.reactionRow}>
                        {nextDay != null && (
                            <span className={styles.reactionItem}>
                                <span>Next day</span> <b className={nextDay >= 0 ? styles.up : styles.down}>{formatPct(nextDay)}</b>
                            </span>
                        )}
                        {weekAfter != null && (
                            <span className={styles.reactionItem}>
                                <span>Week after</span> <b className={weekAfter >= 0 ? styles.up : styles.down}>{formatPct(weekAfter)}</b>
                            </span>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
};

export default EventHero;
